import React from 'react';
import moment from 'moment';
import { Resources, PositionTemplateFieldType } from '@erecruit/types';
import { Intl } from '@erecruit/core';

import {
  PositionSection,
  ModalConfig,
  PositionRecordMatchCounters,
} from './types';
import {
  FormFieldType,
  PositionField,
  PositionFieldSection,
} from '../AddNewPositionWizard/types';

const { FormattedMessage } = Intl;

export type SupportedTypes =
  | Resources.Position
  | Resources.PositionLocation
  | Resources.PositionSchedule
  | Resources.PositionBilling
  | Resources.CustomField[];
interface Mapping {
  fieldType: PositionTemplateFieldType;
  formFieldType: FormFieldType;
  valuePath?: string;
}

export const MATCH_STATUSES_TRANSLATIONS = {
  submittals: (
    <FormattedMessage
      id={messageIds.vms.positionRecord.positionDetails.submittalsSubtitle}
    />
  ),
  interview: (
    <FormattedMessage
      id={messageIds.vms.positionRecord.positionDetails.interviewSubtitle}
    />
  ),
  offers: (
    <FormattedMessage
      id={messageIds.vms.positionRecord.positionDetails.offersSubtitle}
    />
  ),
  candidateAccepted: (
    <FormattedMessage
      id={
        messageIds.vms.positionRecord.positionDetails.candidateAcceptedSubtitle
      }
    />
  ),
  fullPlacement: (
    <FormattedMessage
      id={messageIds.vms.positionRecord.positionDetails.fullPlacementSubtitle}
    />
  ),
};

export const MATCH_STATUSES: Record<
  keyof PositionRecordMatchCounters,
  string
> = {
  submittals: 'submittals',
  interview: 'interview',
  offers: 'offers',
  candidateAccepted: 'candidateAccepted',
  fullPlacement: 'fullPlacement',
};

export const MATCH_STATUS_TYPES_GROUPS = {
  submittals: [
    Resources.MatchStatusType.Matched,
    Resources.MatchStatusType.Present,
    Resources.MatchStatusType.Submittal,
  ],
  interview: [
    Resources.MatchStatusType.Invited,
    Resources.MatchStatusType.FirstRoundInterview,
    Resources.MatchStatusType.PastFirstInterview,
    Resources.MatchStatusType.InterviewsComplete,
  ],
  offers: [
    Resources.MatchStatusType.OfferWillBeMade,
    Resources.MatchStatusType.OfferMade,
    Resources.MatchStatusType.FallOff,
    Resources.MatchStatusType.PartialPlacement,
  ],
  candidateAccepted: [Resources.MatchStatusType.CandidateAccepted],
  fullPlacement: [Resources.MatchStatusType.FullPlacement],
};

const mappings: Record<PositionSection, Record<string, Mapping>> = {
  aboutPosition: {
    [nameof<Resources.Position>(o => o.numberOfPositions)]: {
      fieldType: PositionTemplateFieldType.NumberOfPositions,
      formFieldType: FormFieldType.Number,
    },
    [nameof<Resources.Position>(o => o.positionType)]: {
      fieldType: PositionTemplateFieldType.PositionType,
      formFieldType: FormFieldType.SingleSelect,
      valuePath: nameof<Resources.Position>(o => o.positionType.id),
    },
    [nameof<Resources.Position>(o => o.name)]: {
      fieldType: PositionTemplateFieldType.PositionTitle,
      formFieldType: FormFieldType.Text,
    },
    [nameof<Resources.Position>(o => o.positionDescription)]: {
      fieldType: PositionTemplateFieldType.PositionDescription,
      formFieldType: FormFieldType.TextArea,
    },
    [nameof<Resources.Position>(o => o.workHours)]: {
      fieldType: PositionTemplateFieldType.WorkHours,
      formFieldType: FormFieldType.Text,
    },
  },
  location: {
    [nameof<Resources.PositionLocation>(o => o.workFromHome)]: {
      fieldType: PositionTemplateFieldType.WorkFromHome,
      formFieldType: FormFieldType.Boolean,
    },
    [nameof<Resources.PositionLocation>(o => o.worksiteAddress)]: {
      fieldType: PositionTemplateFieldType.WorksiteAddress,
      formFieldType: FormFieldType.SingleSelect,
      valuePath: nameof<Resources.PositionLocation>(o => o.worksiteAddress.id),
    },
    [nameof<Resources.PositionLocation>(o => o.facilityDepartments)]: {
      fieldType: PositionTemplateFieldType.FacilityDepartments,
      formFieldType: FormFieldType.MultiSelect,
    },
  },
  schedule: {
    [nameof<Resources.PositionSchedule>(o => o.durationAmount)]: {
      fieldType: PositionTemplateFieldType.Duration,
      formFieldType: FormFieldType.Number,
    },
    [nameof<Resources.PositionSchedule>(o => o.startTime)]: {
      fieldType: PositionTemplateFieldType.StartTime,
      formFieldType: FormFieldType.Time,
    },
    [nameof<Resources.PositionSchedule>(o => o.endTime)]: {
      fieldType: PositionTemplateFieldType.EndTime,
      formFieldType: FormFieldType.Time,
    },
    [nameof<Resources.PositionSchedule>(o => o.estimatedStartDate)]: {
      fieldType: PositionTemplateFieldType.EstimatedStartDate,
      formFieldType: FormFieldType.Date,
    },
    [nameof<Resources.PositionSchedule>(o => o.estimatedEndDate)]: {
      fieldType: PositionTemplateFieldType.EstimatedEndDate,
      formFieldType: FormFieldType.Date,
    },
    [nameof<Resources.PositionSchedule>(o => o.shiftTypes)]: {
      fieldType: PositionTemplateFieldType.ShiftTypes,
      formFieldType: FormFieldType.SingleSelect,
    },
  },
  billing: {},
  additionalInfo: {},
};

const convertToPositionFields = <T extends SupportedTypes>(
  section: PositionSection,
  data: T,
): PositionField[] => {
  const sectionMappings = mappings[section];

  const positionFields: (PositionField | undefined)[] = Object.keys(data).map(
    key => {
      let value;
      const mapping = sectionMappings[key];

      if (!mapping) return undefined;

      const { fieldType, formFieldType, valuePath } = mapping;

      if (key === 'shiftTypes') {
        const shiftTypes = (data[
          key as keyof T
        ] as unknown) as Resources.PositionSchedule['shiftTypes'];
        value = shiftTypes[0]?.id;
      } else {
        value = valuePath
          ? (data[key as keyof T] as any)[valuePath] // eslint-disable-line @typescript-eslint/no-explicit-any
          : data[key as keyof T];
      }

      return {
        fieldType,
        formFieldType,
        section: section as PositionFieldSection,
        value,
        settings: { required: false, locked: false },
      };
    },
  );

  return positionFields.filter(
    positionField => !!positionField,
  ) as PositionField[];
};

type SectionTranslationKey = 'general' | 'billing' | 'location' | 'schedule';

export const getModalConfigForSection = <T extends SupportedTypes>(
  section: PositionSection,
  data: T,
) => {
  const translationKey =
    section !== 'aboutPosition'
      ? (section as SectionTranslationKey)
      : 'general';
  const translationId = messageIds.vms.newPositionWizard[translationKey];

  const modalConfig: ModalConfig = {
    id: '',
    formSpec: {
      section,
      description: <FormattedMessage id={translationId.disclaimer} />,
      fields: convertToPositionFields(section, data),
      title: <FormattedMessage id={translationId.title} />,
    },
    title: <FormattedMessage id={translationId.title} />,
    data,
  };

  return modalConfig;
};

const paramMap: { [field: string]: string } = {
  positionType: 'positionTypeId',
  worksiteAddress: 'worksiteAddressId',
};

export const renameKeys = (positionValues: { [field: string]: unknown }) => {
  return Object.entries(positionValues).reduce((acc, [key, value]) => {
    if (key in paramMap) {
      return { ...acc, [paramMap[key]]: Number(value) };
    }
    return { ...acc, [key]: value };
  }, {});
};

export const convertMomentTimeToString = <T, K extends keyof T>(
  value: T,
  convertKeys: K[],
  format = 'HH:mm:ss',
) => {
  return Object.keys(value).reduce((accumulator, key: string) => {
    if (convertKeys.includes(key as K)) {
      return {
        ...accumulator,
        [key]: moment(value[key as K]).format(format),
      };
    } else return { ...accumulator, [key]: value[key as K] };
  }, {});
};
